import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { toast } from '../../store/toastStore';
import { usePermissions } from '../../hooks/usePermissions';
import Dialog from '../ui/Dialog';
import Field, { FormErrorSummary, FormServerError, focusField, firstErrorKey, hasErrors } from '../ui/Field';
import StatusCircles from '../StatusCircles';
import { AdditionalAssigneesCombobox } from '../tasks/TaskAssigneesField';
import TaskAuditLines from '../tasks/TaskAuditLines';
import { resolveTaskAssignees } from '../tasks/taskAssigneesModel';
import { TaskFilesPicker } from '../tasks/TaskFiles';
import { useAlsoAssignQuestion } from '../tasks/useAlsoAssignQuestion';
import { FormFooter } from './formBits';
import { createSubmitGuard, newAttemptKey } from './submitGuardModel';
import { rememberTaskEngagement } from '../../utils/lastTaskProject';
import MissingFinancialsNotice, { missingFinancialsEngagementId, missingFinancialsProjectId } from '../engagement/missingFinancials';
import EngagementChainFields, { LockedChainStrip, LockedValue, milestoneTree, useEngagementChain, useResolvedPlacement, type TaskPlacement } from '../engagements/EngagementChainFields';
import type { ChainLock, ChainValue } from '../engagements/engagementChainModel';
import { getPriorityLabels, type Ticket } from '../../types';
import {
  buildCreateTaskPayload, buildFullEditTaskPayload, dateInputValue, newTaskOwnerId, taskFieldOrder, validateTaskForm,
} from './taskFormModel';
import HideFromCustomerToggle from '../HideFromCustomerToggle';
import EntityPicker from '../ui/EntityPicker';
import { withCurrent } from '../ui/entityPickerModel';
import DateInput from '../ui/DateInput';

/**
 * The one task form (T-46, PRD §6.5 / §8; P-06/A — D-57, D-19, D-44), create
 * and edit. The fields in this order:
 *
 *   שם* · לקוח* · סוג התקשרות* · התקשרות* · אבן דרך (project / development
 *   only, sub-milestones indented) · תיאור · עובדים משויכים · אחראי ·
 *   התחלה, יעד · שעות מוערכות* · עדיפות (רגילה) · סטטוס (circles, בתכנון) ·
 *   📎 צירוף קבצים
 *
 * - Create: opened from a screen, the upper levels of the chain are filled
 *   and locked (`lock`, P-02b-3). ONE request — `createTaskWithFiles`, the
 *   files included (multipart); the server writes the task and its files in
 *   one transaction (T-16). Before it, D-59: "לשייך גם לאבן הדרך ולפרויקט?".
 * - Edit: the chain is fixed (shown with a lock); every other field. The
 *   estimate is optional (D-328; was required by D-19). The
 *   task keeps NULL when blank. New assignees → the same D-59 question. The status here is
 *   the circles; a completed task is reopened from its page (B.23).
 *
 * Callers refresh their own queries in `onCreated` / `onSaved`.
 */
interface EditableTask {
  id: string;
  title?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  agents?: { id: string }[] | null;
  assigned_agent_id?: string | null;
}

export type TaskFormModalProps =
  | {
      mode: 'create';
      placement?: TaskPlacement;
      lock?: ChainLock;
      initialDueDate?: string;
      /**
       * P-37 (D-194 Q-179) — "יצירת משימה מהקריאה": the form opens filled from
       * the ticket (title, description); its files are copied by the server.
       */
      fromTicket?: { id: string; title: string; description?: string | null; filesCount?: number };
      onClose: () => void;
      onCreated: (ticketId: string, placed: { projectId: string | null; engagementId: string }) => void;
    }
  | {
      mode: 'edit';
      /** Kept for the callers; the edit form is the same everywhere since P-06. */
      variant?: 'list' | 'project';
      task: EditableTask;
      organizationId?: string;
      onClose: () => void;
      onSaved: () => void;
    };

const FORM_ID = 'task-form';
const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

export default function TaskFormModal(props: TaskFormModalProps) {
  return props.mode === 'create' ? <CreateTaskGate {...props} /> : <EditTaskGate {...props} />;
}

function useLabels() {
  const { t } = useTranslation();
  return {
    'task-name': t('taskForm.name'),
    'task-customer': t('engagementChain.customer'),
    'task-type': t('engagementChain.type'),
    'task-engagement': t('engagementChain.engagement'),
    'task-end': t('taskForm.dueDate'),
    'task-hours': t('taskForm.estimatedHours'),
  } as Record<string, string>;
}

/** "אחראי": optional; pre-filled with the creator, can be changed or cleared (D-156 #4, was D-57). */
function OwnerSelect({ value, onChange, agents }: { value: string; onChange: (v: string) => void; agents: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  return (
    <Field label={t('taskForm.owner')} id="task-owner" hint={t('taskForm.ownerOptionalHint')}>
      <EntityPicker
        emptyLabel={t('taskForm.noOwner')}
        options={withCurrent(agents.map((a) => ({ value: a.id, label: a.name })), value, t('taskForm.currentOwner'))}
        value={value}
        onChange={onChange}
      />
    </Field>
  );
}

function PrioritySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const labels = getPriorityLabels(t as any);
  return (
    <Field label={t('common.priority')} id="task-priority">
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {PRIORITIES.map((p) => <option key={p} value={p}>{labels[p]}</option>)}
      </select>
    </Field>
  );
}

// ─── Create ──────────────────────────────────────────────────────────────────

function CreateTaskGate(props: Extract<TaskFormModalProps, { mode: 'create' }>) {
  const resolved = useResolvedPlacement(props.placement);
  if (!resolved.ready) return null;
  return <CreateTaskForm {...props} initialChain={resolved.value} />;
}

function CreateTaskForm({ lock, initialChain, initialDueDate, fromTicket, onClose, onCreated }: Extract<TaskFormModalProps, { mode: 'create' }> & { initialChain: ChainValue }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const labels = useLabels();
  // P-33c (D-185): internal-only — on "עבודה פנימית", her own task, no customer / owner / assignee choice.
  const { internalOnly } = usePermissions();

  const [chainValue, setChainValue] = useState<ChainValue>(initialChain);
  // SEC-2b (D-94, D-106) — is the caller a task manager (GM · AM · PM · TL)?
  // A plain employee picks among every OPEN engagement and is the owner.
  const { data: anyPerms } = useQuery({
    queryKey: ['task-create-permissions'],
    queryFn: () => api.getTaskCreatePermissions(),
    staleTime: 5 * 60_000,
    enabled: !!user,
  });
  const selfTaskPicker = anyPerms?.data ? !anyPerms.data.manages_any : false;
  // FIX-P37 (D-194) — "יצירת משימה מהקריאה": the ticket's customer is locked (the engagement stays a choice).
  const chain = useEngagementChain({ value: chainValue, onChange: setChainValue, lock: fromTicket && !lock ? 'customer' : lock, selfTask: selfTaskPicker });
  const { data: herePerms } = useQuery({
    queryKey: ['task-create-permissions', chainValue.organizationId, chain.projectId],
    queryFn: () => api.getTaskCreatePermissions({
      organization_id: chainValue.organizationId || undefined,
      project_id: chain.projectId || undefined,
    }),
    staleTime: 60_000,
    enabled: !!user && !!chainValue.organizationId,
  });
  /** D-94 — not a manager here: the task is the caller's own (the owner is locked). */
  const selfOnly = internalOnly || selfTaskPicker || (herePerms?.data ? !herePerms.data.can_manage : false);

  const [name, setName] = useState(fromTicket?.title ?? '');
  const [description, setDescription] = useState(fromTicket?.description ?? '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(initialDueDate ?? '');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [priority, setPriority] = useState('medium');
  const [status, setStatus] = useState<string>('planning');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [ownerTouched, setOwnerTouched] = useState(false);
  const [pickedOwnerId, setPickedOwnerId] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<unknown>(null);
  const question = useAlsoAssignQuestion();
  // P-56 (D-231 §1) — one create at a time (Enter / a second click during the
  // question or the upload), and one key for the whole attempt so a retry after
  // a lost answer cannot make a second task.
  const guard = useRef(createSubmitGuard(newAttemptKey));
  const financialsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (serverError) financialsRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [serverError]);

  const orgId = chainValue.organizationId;
  const [ownerOrg, setOwnerOrg] = useState(orgId);
  if (ownerOrg !== orgId) {
    setOwnerOrg(orgId);
    setPickedOwnerId('');
    setOwnerTouched(false);
    setAssigneeIds([]);
  }

  const { data: agentsRes, isLoading: agentsLoading } = useQuery({
    queryKey: ['assignable-agents', orgId],
    queryFn: () => api.getAssignableAgents(orgId!),
    staleTime: 60_000,
    enabled: !!orgId && !internalOnly,
  });
  const agents = useMemo(() => agentsRes?.data ?? [], [agentsRes]);
  // P-36 (D-191): the internal-only employee's "חברי צוות" — every OTE employee, the manager included
  // (never a customer's person). The owner is always herself; the server enforces it.
  const { data: teamRes } = useQuery({
    queryKey: ['internal-team-members'],
    queryFn: () => api.getUsers({ ote_staff: true, active: true, limit: 500 }),
    staleTime: 60_000,
    enabled: internalOnly,
  });
  const teamMembers = useMemo(
    () => (teamRes?.data ?? []).filter((u) => (u.role === 'admin' || u.role === 'agent') && u.id !== user?.id),
    [teamRes, user?.id],
  );
  // D-156 #4 — "אחראי" opens with the creator (when they can be picked in this customer); change or clear freely.
  const creatorId = user && agents.some((a) => a.id === user.id) ? user.id : '';
  const ownerId = newTaskOwnerId({ touched: ownerTouched, pickedOwnerId, creatorId });

  const values = () => ({ name, startDate, endDate, chain: chainValue, estimatedHours });
  const validate = () => validateTaskForm(values(), t);
  const onBlurField = (id: string) => setErrors((prev) => ({ ...prev, [id]: validate()[id] }));
  const recheck = (id: string) => { if (errors[id]) setErrors((prev) => ({ ...prev, [id]: validate()[id] })); };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (guard.current.busy) return;
    setServerError(null);
    const next = validate();
    setErrors(next);
    setSubmitted(true);
    if (hasErrors(next)) {
      const first = firstErrorKey(next, taskFieldOrder());
      if (first) focusField(first);
      return;
    }
    const assignees = resolveTaskAssignees({
      creatorOwnerId: selfOnly ? user!.id : null, pickedOwnerId: ownerId, additionalIds: assigneeIds,
    });
    const milestoneId = chain.showMilestone ? chainValue.milestoneId : '';
    const everyone = [assignees.assigned_agent_id, ...(assignees.additional_agent_ids ?? [])].filter(Boolean) as string[];
    guard.current.enter();
    setSaving(true);
    let created: Ticket | undefined;
    try {
      // D-59 — ask before the one save (no question: service / bank / לל"ח, or already in both).
      const alsoAssignParents = await question.ask(
        { milestoneId: milestoneId || undefined, projectId: milestoneId ? undefined : (chain.projectId || undefined) },
        everyone,
        (id) => agents.find((a) => a.id === id)?.name,
      );
      const ticketRes = await api.createTaskWithFiles(buildCreateTaskPayload({
        customerId: user!.id,
        name,
        organizationId: orgId,
        assignees,
        endDate,
        engagementId: chainValue.engagementId,
        milestoneId,
        startDate,
        description,
        estimatedHours,
        priority,
        status,
        alsoAssignParents,
        sourceTicketId: fromTicket?.id,
      }), files, guard.current.key);
      created = ticketRes.data as Ticket | undefined;
      if (!created?.id) {
        created = undefined;
        setServerError({ error: { message: t('taskForm.createFailed') } });
      }
    } catch (err: unknown) {
      // A real failure: a new attempt (new key); the message is shown in the form (scrolled into view).
      if ((err as { response?: unknown })?.response) guard.current.renewKey(); // no answer at all (network): keep the key
      setServerError(err);
    } finally {
      setSaving(false);
      guard.current.leave();
    }
    if (!created) return;
    // From here the task exists: nothing below may show "failed" (a retry would be a duplicate).
    rememberTaskEngagement(user?.id, chainValue.engagementId);
    // D-231 §2 — a short notice that goes away by itself; the workers told = everyone but the creator (D-59).
    const told = new Set(everyone.filter((id) => id !== user?.id)).size;
    toast('success', told > 0 ? t('taskForm.createdNotified', { count: told }) : t('taskForm.created'));
    try {
      onCreated(created.id, { projectId: created.project_id ?? null, engagementId: chainValue.engagementId });
    } catch (err) {
      // The caller's refresh failed — the task is saved; close anyway.
      console.error('onCreated failed', err);
      onClose();
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={fromTicket ? t('replyTimer.taskForm.title') : t('taskForm.titleNew')}
      size="lg"
      dismissible={!saving}
      footer={(
        <FormFooter formId={FORM_ID} busy={saving} submitLabel={t('taskForm.create')} onCancel={onClose} disabled={!!orgId && agentsLoading} />
      )}
    >
      {/* D-316 — compact: the locked levels in one line on top, short fields two in a row. */}
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="space-y-3">
        {submitted && <FormErrorSummary errors={errors} labels={labels} order={taskFieldOrder()} />}
        <LockedChainStrip chain={chain} />
        {missingFinancialsEngagementId(serverError) ? (
          <div ref={financialsRef}>
            <MissingFinancialsNotice engagementId={missingFinancialsEngagementId(serverError)!} projectId={missingFinancialsProjectId(serverError)} />
          </div>
        ) : (
          <FormServerError error={serverError} fallback={t('taskForm.createFailed')} />
        )}

        {/* 1. שם* */}
        <Field label={t('taskForm.name')} id="task-name" required error={errors['task-name']}>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => { setName(e.target.value); recheck('task-name'); }}
            onBlur={() => onBlurField('task-name')}
            placeholder={t('taskForm.namePlaceholder')}
            autoComplete="off"
          />
        </Field>

        {/* 2–5. לקוח* · סוג* · התקשרות* · אבן דרך (the locked ones are in the line on top) */}
        <div className="grid grid-cols-1 gap-3 empty:hidden sm:grid-cols-2">
          <EngagementChainFields
            chain={chain}
            idPrefix="task"
            errors={errors}
            onBlurField={onBlurField}
            onFieldChange={(id) => { if (errors[id]) setErrors((prev) => ({ ...prev, [id]: undefined })); }}
            hideLocked
          />
        </div>

        {/* 6. תיאור */}
        <Field label={t('common.description')} id="task-desc">
          <textarea className="input min-h-16 resize-y text-body-long" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        {/* 7–8. עובדים משויכים · אחראי */}
        {internalOnly ? (
          <AdditionalAssigneesCombobox agents={teamMembers} ownerId={user!.id} selectedIds={assigneeIds} onChange={setAssigneeIds} label={t('taskForm.teamMembers')} />
        ) : orgId ? (
          agentsLoading ? (
            <p className="text-sm text-text-2" aria-live="polite">{t('taskAssignees.loading')}</p>
          ) : (
            <div className="space-y-3">
              <AdditionalAssigneesCombobox agents={agents} ownerId={selfOnly ? user!.id : ''} selectedIds={assigneeIds} onChange={setAssigneeIds} label={t('taskForm.assignees')} />
              {selfOnly ? (
                // SEC-2b (D-94) — a plain employee's task is their own.
                <LockedValue label={t('taskForm.owner')} id="task-owner" text={user?.name || t('taskForm.ownerSelf')} />
              ) : (
                <OwnerSelect value={ownerId} onChange={(v) => { setOwnerTouched(true); setPickedOwnerId(v); }} agents={agents} />
              )}
            </div>
          )
        ) : (
          <p className="text-sm text-text-2">{t('taskForm.pickCustomerFirst')}</p>
        )}

        {/* 9. התחלה, יעד */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('taskForm.startDate')} id="task-start">
            <DateInput className="input" value={startDate} onChange={(e) => { setStartDate(e.target.value); recheck('task-end'); }} onBlur={() => onBlurField('task-end')} />
          </Field>
          <Field label={t('taskForm.dueDate')} id="task-end" error={errors['task-end']}>
            <DateInput className="input" value={endDate} onChange={(e) => { setEndDate(e.target.value); recheck('task-end'); }} onBlur={() => onBlurField('task-end')} />
          </Field>
        </div>

        {/* 10–11. שעות מוערכות* · עדיפות */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('taskForm.estimatedHours')} id="task-hours" error={errors['task-hours']}>
            <input
              type="number"
              inputMode="decimal"
              step="0.25"
              min="0"
              className="input"
              value={estimatedHours}
              onChange={(e) => { setEstimatedHours(e.target.value); recheck('task-hours'); }}
              onBlur={() => onBlurField('task-hours')}
            />
          </Field>
          <PrioritySelect value={priority} onChange={setPriority} />
        </div>

        {/* 12. סטטוס */}
        <fieldset>
          <legend className="label">{t('common.status')}</legend>
          <StatusCircles status={status} onChange={setStatus} label={t('common.status')} />
        </fieldset>

        {/* 13. 📎 צירוף קבצים */}
        <TaskFilesPicker files={files} onChange={setFiles} />
        {/* P-37 (Q-179) — the ticket's own files go with it. */}
        {fromTicket && (fromTicket.filesCount ?? 0) > 0 && (
          <p className="text-xs text-text-2">{t('replyTimer.taskForm.filesFromTicket', { count: fromTicket.filesCount })}</p>
        )}
      </form>
      {question.dialog}
    </Dialog>
  );
}

// ─── Edit ────────────────────────────────────────────────────────────────────

function EditTaskGate(props: Extract<TaskFormModalProps, { mode: 'edit' }>) {
  const { data } = useQuery({
    queryKey: ['ticket', props.task.id],
    queryFn: () => api.getTicket(props.task.id),
    staleTime: 30_000,
  });
  const full = data?.data;
  if (!full) return null;
  return <EditTaskForm {...props} full={full} />;
}

function EditTaskForm({ full, onClose, onSaved }: Extract<TaskFormModalProps, { mode: 'edit' }> & { full: Ticket }) {
  const { t } = useTranslation();
  const labels = useLabels();
  const orgId = full.organization_id;
  const { data: agentsRes } = useQuery({
    queryKey: ['assignable-agents', orgId],
    queryFn: () => api.getAssignableAgents(orgId),
    staleTime: 60_000,
    enabled: !!orgId,
  });
  const agents = useMemo(() => agentsRes?.data ?? [], [agentsRes]);
  const isProjectKind = !!full.project_id && (!full.engagement_type || full.engagement_type === 'project' || full.engagement_type === 'development');
  const { data: msRes } = useQuery({
    // D-280 (D-63): the same milestone choices as the new-task form.
    queryKey: ['milestones', full.project_id, 'task-choices'],
    queryFn: () => api.getMilestoneTaskChoices(full.project_id!),
    staleTime: 60_000,
    enabled: isProjectKind,
  });
  const milestones = useMemo(() => (msRes?.data ?? []).filter((m: any) => !m.archived_at || m.id === full.milestone_id), [msRes, full.milestone_id]);

  const initialAgents = useMemo(() => (full.agents ?? []).map((a) => a.id), [full.agents]);
  const [name, setName] = useState(full.title || '');
  const [description, setDescription] = useState(full.description || '');
  const [milestoneId, setMilestoneId] = useState(full.milestone_id || '');
  const [ownerId, setOwnerId] = useState(full.assigned_agent_id || '');
  const [assigneeIds, setAssigneeIds] = useState<string[]>(initialAgents.filter((id) => id !== full.assigned_agent_id));
  const [startDate, setStartDate] = useState(() => dateInputValue(full.start_date));
  const [dueDate, setDueDate] = useState(() => dateInputValue(full.due_date));
  const [estimatedHours, setEstimatedHours] = useState(full.estimated_hours != null && Number(full.estimated_hours) > 0 ? String(Number(full.estimated_hours)) : '');
  const [priority, setPriority] = useState<string>(full.priority || 'medium');
  const [status, setStatus] = useState<string>(full.status);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<unknown>(null);
  const question = useAlsoAssignQuestion();
  const completed = full.status === 'completed';

  const validate = () => validateTaskForm({ name, startDate, endDate: dueDate, estimatedHours }, t);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const next = validate();
    setErrors(next);
    setSubmitted(true);
    if (hasErrors(next)) { focusField(firstErrorKey(next, taskFieldOrder()) ?? 'task-name'); return; }
    setSaving(true);
    try {
      // The milestone (inside the same engagement only, D-37) moves through /move.
      if (isProjectKind && (milestoneId || '') !== (full.milestone_id || '')) {
        await api.moveTicket(full.id, { project_id: full.project_id ?? null, milestone_id: milestoneId || null });
      }
      const all = [...new Set([...(ownerId ? [ownerId] : []), ...assigneeIds])];
      const added = all.filter((id) => !initialAgents.includes(id));
      const alsoAssignParents = added.length > 0
        ? await question.ask({ ticketId: full.id }, added, (id) => agents.find((a) => a.id === id)?.name)
        : false;
      await api.updateTicket(full.id, buildFullEditTaskPayload({
        name, description, ownerId, assigneeIds, startDate, dueDate, estimatedHours, priority,
        status, initialStatus: full.status, alsoAssignParents,
      }) as any);
      onSaved();
    } catch (err: unknown) {
      setServerError(err);
    } finally {
      setSaving(false);
    }
  };

  const engagementText = full.engagement_name
    ? `${full.engagement_name}${full.engagement_type ? ` · ${t(`engagementDetail.types.${full.engagement_type}`)}` : ''}`
    : (full.project_name ?? '');

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('taskForm.titleEdit')}
      size="lg"
      dismissible={!saving}
      footer={<FormFooter formId={FORM_ID} busy={saving} submitLabel={t('taskForm.save')} onCancel={onClose} />}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="space-y-4">
        {submitted && <FormErrorSummary errors={errors} labels={labels} order={taskFieldOrder()} />}
        <FormServerError error={serverError} fallback={t('taskForm.saveFailed')} />

        <Field label={t('taskForm.name')} id="task-name" required error={errors['task-name']}>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => { setName(e.target.value); if (errors['task-name']) setErrors((p) => ({ ...p, 'task-name': validate()['task-name'] })); }}
            onBlur={() => setErrors((p) => ({ ...p, 'task-name': validate()['task-name'] }))}
            autoComplete="off"
          />
        </Field>

        {/* The chain is fixed (D-37: a task never changes engagement). */}
        <LockedValue id="task-customer" label={t('engagementChain.customer')} text={full.organization_name ?? ''} />
        <LockedValue id="task-engagement" label={t('engagementChain.engagement')} text={engagementText} />
        {isProjectKind && (
          <Field label={t('taskForm.milestone')} id="task-milestone">
            <EntityPicker
              emptyLabel={t('engagementChain.noMilestone')}
              options={milestoneTree(milestones as any[]).map(({ m, depth }) => ({ value: m.id, label: m.name, depth }))}
              value={milestoneId}
              onChange={setMilestoneId}
            />
          </Field>
        )}

        <Field label={t('common.description')} id="task-desc">
          <textarea className="input min-h-20 text-body-long" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <AdditionalAssigneesCombobox agents={agents} ownerId="" selectedIds={assigneeIds} onChange={setAssigneeIds} label={t('taskForm.assignees')} />
        {/* DQ-170 (D-94): a plain employee does not swap the owner - the field is read only unless the server said the viewer manages the task. */}
        {(full as { can_manage_task?: boolean }).can_manage_task === false ? (
          <LockedValue label={t('taskForm.owner')} id="task-owner" text={agents.find((a) => a.id === ownerId)?.name || (full as { agent_name?: string }).agent_name || t('taskForm.noOwner')} />
        ) : (
          <OwnerSelect value={ownerId} onChange={setOwnerId} agents={agents} />
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('taskForm.startDate')} id="task-start">
            <DateInput className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={t('taskForm.dueDate')} id="task-end" error={errors['task-end']}>
            <DateInput className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} onBlur={() => setErrors((p) => ({ ...p, 'task-end': validate()['task-end'] }))} />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label={t('taskForm.estimatedHours')}
            id="task-hours"
            error={errors['task-hours']}
          >
            <input
              type="number"
              inputMode="decimal"
              step="0.25"
              min="0"
              className="input"
              value={estimatedHours}
              onChange={(e) => { setEstimatedHours(e.target.value); if (errors['task-hours']) setErrors((p) => ({ ...p, 'task-hours': undefined })); }}
              onBlur={() => { if (estimatedHours !== '') setErrors((p) => ({ ...p, 'task-hours': validate()['task-hours'] })); }}
            />
          </Field>
          <PrioritySelect value={priority} onChange={setPriority} />
        </div>

        <fieldset>
          <legend className="label">{t('common.status')}</legend>
          {completed
            ? <StatusCircles status={status} label={t('common.status')} />
            : <StatusCircles status={status} onChange={setStatus} label={t('common.status')} />}
          {completed && <p className="mt-1 text-xs text-text-2">{t('ticketDetail.statusLockedCompleted')}</p>}
        </fieldset>

        <TaskAuditLines task={full} className="border-t border-border pt-3" />
        <HideFromCustomerToggle entity="task" id={full.id} />
      </form>
      {question.dialog}
    </Dialog>
  );
}
